import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowLeft, Calendar, Gauge, ExternalLink, Phone, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { api, formatEuro, formatKm } from "@/lib/api";
import LoanCalculator from "@/components/LoanCalculator";
import FuelBadge from "@/components/FuelBadge";

export default function CarDetail() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [waOpen, setWaOpen] = useState(false);

  const sellers = [
    { name: "Ville Paakkari", phone: "044 354 9300", wa: "358443549300" },
    { name: "Teijo Paakkari", phone: "0500 653 999", wa: "358500653999" },
  ];

  useEffect(() => {
    setCar(null);
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setNotFound(true));
  }, [id]);

  // Keyboard navigation for the gallery (← / →)
  useEffect(() => {
    const onKey = (e) => {
      if (!car) return;
      const imgs = car.details?.images?.length ? car.details.images : [car.image].filter(Boolean);
      if (imgs.length < 2) return;
      if (e.key === "ArrowRight") setActive((a) => (a + 1) % imgs.length);
      if (e.key === "ArrowLeft") setActive((a) => (a - 1 + imgs.length) % imgs.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [car]);

  if (notFound)
    return (
      <div className="pt-40 pb-24 max-w-7xl mx-auto px-6 text-center">
        <h1 className="font-display text-4xl mb-4">Autoa ei löytynyt</h1>
        <Link to="/autot" className="text-[#ff5a00] font-mono text-sm">← Takaisin autoihin</Link>
      </div>
    );

  if (!car)
    return <div className="pt-40 pb-24 max-w-7xl mx-auto px-6 font-mono text-white/40">Ladataan…</div>;

  const gallery = car.details?.images?.length ? car.details.images : [car.image].filter(Boolean);
  const go = (dir) => setActive((a) => (a + dir + gallery.length) % gallery.length);
  const specs = car.details?.specs || {};
  const specItems = [
    { icon: Calendar, label: "Vuosimalli", value: car.year },
    { icon: Gauge, label: "Mittarilukema", value: formatKm(car.mileage) },
    ...(specs.fuel ? [{ label: "Käyttövoima", value: specs.fuel }] : []),
    ...(specs.transmission ? [{ label: "Vaihteisto", value: specs.transmission }] : []),
    ...(specs.drivetrain ? [{ label: "Vetotapa", value: specs.drivetrain }] : []),
    ...(specs.body ? [{ label: "Kori", value: specs.body }] : []),
    ...(specs.color ? [{ label: "Väri", value: specs.color }] : []),
  ];

  return (
    <div className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/autot" data-testid="back-to-inventory"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/50 hover:text-[#ff5a00] transition-colors mb-8">
          <ArrowLeft size={16} /> Autot
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Gallery */}
          <div className="lg:col-span-3">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
              className="group relative aspect-[16/10] bg-black overflow-hidden border border-white/10">
              {gallery[active] ? (
                <img src={gallery[active]} alt={car.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/30 font-mono">EI KUVAA</div>
              )}
              <div className="absolute inset-0 shadow-[inset_0_0_120px_rgba(0,0,0,0.6)] pointer-events-none" />

              {gallery.length > 1 && (
                <>
                  <button
                    onClick={() => go(-1)}
                    data-testid="gallery-prev"
                    aria-label="Edellinen kuva"
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-white opacity-100 lg:opacity-0 lg:group-hover:opacity-100 hover:bg-[#ff5a00] hover:border-[#ff5a00] hover:text-black transition-all duration-300 active:scale-95"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    onClick={() => go(1)}
                    data-testid="gallery-next"
                    aria-label="Seuraava kuva"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/15 text-white opacity-100 lg:opacity-0 lg:group-hover:opacity-100 hover:bg-[#ff5a00] hover:border-[#ff5a00] hover:text-black transition-all duration-300 active:scale-95"
                  >
                    <ChevronRight size={22} />
                  </button>
                  <div
                    data-testid="gallery-counter"
                    className="absolute bottom-3 right-3 font-mono text-[11px] tracking-widest text-white bg-black/50 backdrop-blur-md border border-white/10 px-3 py-1"
                  >
                    {active + 1} / {gallery.length}
                  </div>
                </>
              )}
            </motion.div>
            {gallery.length > 1 && (
              <div className="grid grid-cols-5 gap-3 mt-3">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                    className={`aspect-[4/3] overflow-hidden border ${active === i ? "border-[#ff5a00]" : "border-white/10"}`}>
                    <img src={g} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sticky info */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-28">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff5a00] mb-3">{car.brand}</p>
              <h1 className="font-display text-4xl md:text-5xl tracking-tight" data-testid="car-title">{car.title}</h1>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {car.mallitarkenne && (
                  <span className="font-mono text-sm text-white/70" data-testid="car-mallitarkenne">{car.mallitarkenne}</span>
                )}
                <FuelBadge fuel={car.fuel} />
              </div>
              <p className="font-display text-6xl md:text-7xl font-semibold text-[#ff5a00] mt-6" data-testid="car-price">{formatEuro(car.price)}</p>

              <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10 mt-8">
                {specItems.map((s, i) => (
                  <div key={i} className="bg-[#050505] p-5">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">{s.label}</p>
                    <p className="text-white mt-1 font-medium">{s.value || "—"}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-3">
                <Link to="/yhteystiedot" data-testid="car-contact-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#ff5a00] text-white font-mono text-xs uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300">
                  <Phone size={16} /> Ota yhteyttä
                </Link>
                <div className="relative">
                  <button onClick={() => setWaOpen((v) => !v)} data-testid="car-whatsapp"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-black font-mono text-xs uppercase tracking-widest px-8 py-4 hover:bg-[#2ee372] transition-colors duration-300">
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </button>
                  {waOpen && (
                    <div className="mt-2 border border-white/10 bg-[#121212]" data-testid="whatsapp-options">
                      <p className="px-4 pt-3 pb-2 font-mono text-[10px] uppercase tracking-widest text-white/40">Valitse myyjä</p>
                      {sellers.map((s) => (
                        <a key={s.wa}
                          href={`https://wa.me/${s.wa}?text=${encodeURIComponent(`Hei ${s.name.split(" ")[0]}, olen kiinnostunut autosta ${car.title} — ${car.url}`)}`}
                          target="_blank" rel="noreferrer" data-testid={`car-whatsapp-${s.wa}`}
                          className="flex items-center justify-between px-4 py-3 text-sm text-white hover:bg-[#25D366] hover:text-black transition-colors border-t border-white/5">
                          <span>{s.name}</span>
                          <span className="font-mono text-xs opacity-70">{s.phone}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <a href={car.url} target="_blank" rel="noreferrer" data-testid="car-nettiauto-link"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-mono text-xs uppercase tracking-widest px-8 py-4 hover:border-[#ff5a00] transition-colors duration-300">
                  <ExternalLink size={16} /> Katso Nettiautossa
                </a>
              </div>
            </div>
          </div>
        </div>

        {car.details?.description && (
          <div className="mt-16 max-w-3xl">
            <h2 className="font-display text-2xl mb-4 tracking-tight">Lisätiedot</h2>
            <p className="text-white/60 leading-relaxed whitespace-pre-line">{car.details.description}</p>
          </div>
        )}

        {car.details?.varusteet?.length > 0 && (
          <div className="mt-16 max-w-5xl" data-testid="varusteet">
            <h2 className="font-display text-2xl mb-8 tracking-tight">Varusteet</h2>
            <div className="space-y-8">
              {car.details.varusteet.map((g, i) => (
                <div key={i}>
                  {g.category && (
                    <p className="font-mono text-xs uppercase tracking-widest text-[#ff5a00] mb-3">{g.category}</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1">
                    {g.items.map((it, j) => (
                      <div key={j} className="flex gap-2 text-white/70 text-sm border-b border-white/5 py-2">
                        <Check size={14} className="text-[#ff5a00] mt-1 shrink-0" /> {it}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-20">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-8">Laske rahoitus tälle autolle</h2>
          <LoanCalculator initialPrice={car.price || 15000} />
        </div>
      </div>
    </div>
  );
}
